import {differenceInSeconds} from 'date-fns';
import {Attributes, AttributesRow, Card} from 'platform/components';
import {HStack, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {TelephonyCallDetail} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {formatCallDuration} from '@dms/shared';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

interface CallTimelineProps extends TestIdProps {
  call: TelephonyCallDetail;
}

/** Rang → Answered → Ended, each with its clock time and the time since the step before. */
export function CallTimeline(props: CallTimelineProps) {
  const formatDateTime = useDateTimeFormatter();

  const renderStep = (at: string, previousAt: string | null) => (
    <HStack spacing={2} align="center" wrap>
      <Text size="small">{formatDateTime('timeMedium', parseDate(at))}</Text>
      <Show when={isNotNil(previousAt) ? previousAt : null}>
        {(previous) => (
          <Text size="small" color="tertiary">
            {i18n.t('page.telephony.callDetail.timelineAfter', {
              duration: formatCallDuration(differenceInSeconds(parseDate(at), parseDate(previous))),
            })}
          </Text>
        )}
      </Show>
    </HStack>
  );

  const answeredAt = props.call.answeredAt;

  const rows: AttributesRow[] = [
    {
      label: i18n.t('page.telephony.callDetail.timelineRang'),
      content: renderStep(props.call.startedAt, null),
      testId: 'rang',
    },
    ...(isNotNil(answeredAt)
      ? [
          {
            label: i18n.t('page.telephony.callDetail.timelineAnswered'),
            content: renderStep(answeredAt, props.call.startedAt),
            testId: 'answered',
          },
        ]
      : []),
    {
      label: i18n.t('page.telephony.callDetail.timelineEnded'),
      // A missed call ends straight after ringing.
      content: renderStep(props.call.endedAt, answeredAt ?? props.call.startedAt),
      testId: 'ended',
    },
  ];

  return (
    <Card title={i18n.t('page.telephony.panel.title')} data-testid={props['data-testid']}>
      <Attributes size="third" rows={rows} data-testid={suffixTestId('steps', props)} />
    </Card>
  );
}
